import { useMissionControlStore } from '../store.js';
import type { Lane, LaneStatus } from '../types.js';

const STATUS_LABEL: Record<LaneStatus, string> = {
  idle: 'Idle',
  planning: 'Planning',
  running: 'Running',
  waiting_approval: 'Needs approval',
  review: 'In review',
  done: 'Done',
  failed: 'Failed',
};

const ROLE_GLYPH: Record<string, string> = {
  coordinator: 'CO',
  builder: 'BL',
  scout: 'SC',
  reviewer: 'RV',
  tester: 'TS',
  documenter: 'DC',
};

export function LaneGrid({ lanes }: { lanes: Lane[] }) {
  const activeLaneId = useMissionControlStore((s) => s.activeLaneId);
  const setActiveLane = useMissionControlStore((s) => s.setActiveLane);

  if (lanes.length === 0) {
    return (
      <section className="panel lane-grid-empty" aria-label="Lanes">
        <p>Select or create a workspace to spawn lanes.</p>
      </section>
    );
  }

  return (
    <section className="panel lane-grid" aria-label="Lanes">
      <div className="section-heading">
        <span className="section-kicker">Live lanes</span>
        <strong>{lanes.length} agents in the swarm</strong>
      </div>

      <div className="lane-grid-inner">
        {lanes.map((lane) => (
          <button
            key={lane.id}
            type="button"
            className={`lane-card lane-status-${lane.status} ${lane.id === activeLaneId ? 'lane-active' : ''}`}
            onClick={() => setActiveLane(lane.id)}
            aria-label={`${lane.name} — ${STATUS_LABEL[lane.status]}`}
          >
            <div className="lane-head">
              <span className="lane-glyph" aria-hidden>
                {ROLE_GLYPH[lane.role] ?? lane.role.slice(0, 2).toUpperCase()}
              </span>
              <div className="lane-titles">
                <strong>{lane.name}</strong>
                <span>
                  {lane.provider} · {lane.model}
                </span>
              </div>
              <span className={`lane-status-pill lane-status-pill-${lane.status}`}>
                {STATUS_LABEL[lane.status]}
              </span>
            </div>
            <p className="lane-task">{lane.currentTask}</p>
            <div className="lane-log-preview">
              {lane.logs.slice(-3).map((log) => (
                <div key={log.at} className={`lane-log lane-log-${log.tone}`}>
                  {log.text}
                </div>
              ))}
              {lane.logs.length === 0 ? (
                <div className="lane-log-empty">Waiting for first output…</div>
              ) : null}
            </div>
          </button>
        ))}
      </div>
    </section>
  );
}
